import { useCallback } from 'react';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { nextStep, type ResolveContext } from '@delaybuddy/contract';
import { SCREENS, type BackTarget, type ScreenId } from './registry';
import { queryKeys, useApi, type Enquiry, type NextStep } from '../lib/api';
import { currentEnquiry, draftEnquiry, navContext, tripDraft, type NavContext } from '../lib/funnel';

/** The static homepage (S0 lives on delay-buddy-new, outside this app). */
export const HOME_URL: string = import.meta.env.VITE_HOME_URL ?? '/';

type Params = Record<string, string | undefined>;

/** Path for a screen under the /claim basename, e.g. pathFor('S7', { id }) → '/e/01…/contact'. */
export function pathFor(id: ScreenId, params: Params = {}): string {
  const def = SCREENS[id];
  const path = def.path ?? (def.host ? SCREENS[def.host].path : null);
  if (!path) throw new Error(`Screen ${id} has no path`);
  return generatePath(path, params as Record<string, string>);
}

export interface GoOptions {
  /** Merged over the current route params (so `:id` carries over by default). */
  params?: Params;
  search?: string;
  replace?: boolean;
}

/**
 * `const go = useGo(); go('S2')`. Pushes a history entry (never a hash: the prototype's
 * fix — the browser must never jump to an element id). The shell scrolls to the top.
 */
export function useGo() {
  const navigate = useNavigate();
  const current = useParams();
  return useCallback(
    (target: ScreenId | 'home', opts: GoOptions = {}) => {
      if (target === 'home') {
        window.location.assign(HOME_URL);
        return;
      }
      const path = pathFor(target, { ...current, ...opts.params });
      navigate(opts.search ? `${path}${opts.search.startsWith('?') ? '' : '?'}${opts.search}` : path, {
        replace: opts.replace,
      });
    },
    [navigate, current],
  );
}

export type { BackTarget };

/* ── Resolver-driven navigation ──────────────────────────── */

/**
 * Where a resolver step lives: its screen, path and query string. Steps that are variants of
 * a screen say which variant in the query: S3 `?leg=1` (a later leg), S11 `?handoff=2`
 * (the S11-handoff sheet for passenger 2), S11 `?for=3` (S11-sign-for, signing for minor 3).
 * S8-duplicate is S8 (it reads `enquiry.duplicateOf`); S12's variant comes from `getClaim`.
 */
export function stepLocation(
  next: NextStep,
  enquiryId: string,
): { screen: ScreenId; path: string; search: string } {
  const at = (screen: ScreenId, params: Params = {}, search = '') => ({
    screen,
    path: pathFor(screen, { id: enquiryId, ...params }),
    search: search ? `?${search}` : '',
  });
  switch (next.step) {
    case 'S3':
      return at('S3', {}, next.leg ? `leg=${next.leg}` : '');
    case 'S8-duplicate':
      return at('S8');
    case 'S11-sign-for':
      return at('S11', {}, `for=${next.passenger}`);
    case 'S11-handoff':
      return at('S11', {}, `handoff=${next.passenger}`);
    case 'S13':
      return at('S13', { item: next.itemKey ?? 'id' });
    case 'S15':
      return at('S15', { passenger: `p${next.passenger}` });
    default:
      return at(next.step);
  }
}

/** `stepLocation` as one string (for `<Navigate to>` and links). */
export const stepHref = (next: NextStep, enquiryId: string) => {
  const { path, search } = stepLocation(next, enquiryId);
  return `${path}${search}`;
};

/** The variant parameters of the current URL (see `stepLocation`). */
export function useStepParams(): { leg: number; handoff: number | null; signFor: number | null } {
  const [qs] = useSearchParams();
  const int = (k: string) => {
    const v = Number(qs.get(k));
    return Number.isInteger(v) && v > 0 ? v : null;
  };
  return { leg: int('leg') ?? 0, handoff: int('handoff'), signFor: int('for') };
}

/** Navigate to a resolver step. */
export function useGoStep() {
  const navigate = useNavigate();
  return useCallback(
    (next: NextStep, enquiryId: string, opts: { replace?: boolean } = {}) =>
      navigate(stepHref(next, enquiryId), opts),
    [navigate],
  );
}

/** The resolver context of this tab (first pass / returning, what a link was for). */
export function resolveContext(nav: NavContext, overrides: ResolveContext = {}): ResolveContext {
  return {
    visit: nav.visit,
    ...(nav.target && nav.target.kind !== 'passenger_part' ? { target: nav.target } : {}),
    ...(nav.passenger ? { scope: { role: 'passenger' as const, passenger: nav.passenger } } : {}),
    ...overrides,
  };
}

/**
 * "What comes next" is the resolver's call, never the screen's:
 *
 * ```ts
 * const { goNext } = useNextStep();
 * const e = await setLegalAnswer.mutateAsync(answer);
 * await goNext(e);            // runs contract/src/resolver.ts on the fresh enquiry: S7, S6-stop…
 * ```
 *
 * `goNext(enquiry?)` resolves on the enquiry you pass (a mutation's answer), else the cached
 * enquiry of this screen, else asks the API (`getNextStep`); before S3 creates the enquiry
 * it resolves on the S1/S2 draft. The context (first pass or returning, the link's target)
 * comes from this tab's `navContext`; pass `ctx` to override it. Returns the step.
 */
export function useNextStep() {
  const api = useApi();
  const qc = useQueryClient();
  const goStep = useGoStep();
  const { id: routeId } = useParams();

  const resolve = useCallback(
    async (enquiry?: Enquiry, ctx: ResolveContext = {}): Promise<{ next: NextStep; enquiryId: string }> => {
      const context = resolveContext(navContext.get(), ctx);
      if (enquiry) return { next: nextStep(enquiry, context), enquiryId: enquiry.id };
      const id = routeId ?? currentEnquiry.get();
      if (!id) return { next: nextStep(draftEnquiry(tripDraft.get()), context), enquiryId: '' };
      const cached = qc.getQueryData<Enquiry>(queryKeys.enquiry(id));
      if (cached) return { next: nextStep(cached, context), enquiryId: id };
      const t = context.target;
      const next = await api.getNextStep(id, {
        visit: context.visit,
        ...(t?.kind === 'claim' ? { target: 'claim' as const } : {}),
        ...(t?.kind === 'item' ? { target: 'item' as const, item: t.itemKey } : {}),
      });
      return { next, enquiryId: id };
    },
    [api, qc, routeId],
  );

  const goNext = useCallback(
    async (enquiry?: Enquiry, opts: { ctx?: ResolveContext; replace?: boolean } = {}) => {
      const { next, enquiryId } = await resolve(enquiry, opts.ctx);
      goStep(next, enquiryId, { replace: opts.replace });
      return next;
    },
    [resolve, goStep],
  );

  return { goNext, resolve };
}
